import type { components } from '@multaqa/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/lib/api'

/**
 * The serial number register (WP11): every numbered unit in the warehouses
 * the reader reaches, and the company's standard warranty. Keys live under
 * ['inventory', company, 'serial-units'].
 */

export type SerialUnit = components['schemas']['SerialUnitResource']
export const SERIAL_STATUSES = [
  'in_stock',
  'in_transit',
  'damaged',
  'sold',
  'written_off',
  'lost',
  'returned_to_supplier',
] as const
export type SerialStatus = (typeof SERIAL_STATUSES)[number]

export type SerialFilters = {
  status?: SerialStatus
  q?: string
  /** WP11: the units of one product, for the product page. */
  productId?: string
  condition?: string
  page?: number
  perPage?: number
}

/** PRD section 24: what state a unit is in, apart from where it is. */
export const SERIAL_CONDITIONS = [
  'new',
  'used',
  'damaged',
  'under_inspection',
  'defective',
] as const

/** PRD section 37: the company's serial switches, in the order the settings show them. */
export const SERIAL_OPTIONS = [
  'enabled',
  'allow_manual_entry',
  'allow_scanning',
  'require_on_receiving',
  'require_on_transfers',
  'require_on_sales',
  'require_on_adjustments',
  'require_on_counts',
] as const

const settingsSchema = z.object({
  data: z.object({
    warranty_months: z.number().nullable(),
    options: z.record(z.string(), z.boolean()).default({}),
  }),
})

export function useSerialUnits(companyId: string | null, filters: SerialFilters) {
  return useQuery({
    queryKey: ['inventory', companyId, 'serial-units', filters] as const,
    enabled: Boolean(companyId),
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const response = await api.GET('/inventory/serial-units', {
        headers: { 'X-Company-Id': companyId ?? '' },
        params: {
          query: {
            status: filters.status,
            product_id: filters.productId,
            condition: filters.condition,
            search: filters.q,
            page: filters.page,
            per_page: filters.perPage ?? 25,
          },
        },
      })
      if (!response.data) throw new Error('GET /inventory/serial-units returned no body')
      return response.data
    },
  })
}

export function useSerialUnit(companyId: string | null, unitId: string) {
  return useQuery({
    queryKey: ['inventory', companyId, 'serial-units', 'detail', unitId] as const,
    enabled: Boolean(companyId),
    queryFn: async (): Promise<SerialUnit> => {
      const response = await api.GET('/inventory/serial-units/{serialUnit}', {
        headers: { 'X-Company-Id': companyId ?? '' },
        params: { path: { serialUnit: unitId } },
      })
      if (!response.data)
        throw new Error('GET /inventory/serial-units/{serialUnit} returned no body')
      return response.data.data
    },
  })
}

export function useAmendSerialUnit(companyId: string, unitId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (body: {
      condition?: string
      warranty_starts_on?: string | null
      warranty_ends_on?: string | null
    }) => {
      const response = await api.PATCH('/inventory/serial-units/{serialUnit}', {
        headers: { 'X-Company-Id': companyId },
        params: { path: { serialUnit: unitId } },
        body,
      })
      if (response.error) throw response.error
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['inventory', companyId, 'serial-units'] }),
  })
}

export function useSerialSettings(companyId: string | null) {
  return useQuery({
    queryKey: ['inventory', companyId, 'serial-settings'] as const,
    enabled: Boolean(companyId),
    queryFn: async () => {
      const response = await api.GET('/inventory/serial-settings', {
        headers: { 'X-Company-Id': companyId ?? '' },
      })
      return settingsSchema.parse(response.data).data
    },
  })
}

export function useSaveSerialSettings(companyId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: {
      warrantyMonths: number | null
      options?: Record<string, boolean>
    }) => {
      const response = await api.PUT('/inventory/serial-settings', {
        headers: { 'X-Company-Id': companyId },
        body: {
          warranty_months: input.warrantyMonths,
          ...(input.options ? { options: input.options } : {}),
        },
      })
      return settingsSchema.parse(response.data).data
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['inventory', companyId, 'serial-settings'] }),
  })
}
